import { startCase } from 'es-toolkit';

import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import {
  LoadDefinitionWrapper,
  LoadWrapper,
  ManualLoadSettings,
  ModelCategory,
  Point2D,
} from '@paragon/contract-library';
import { distributedForceEquals, stressEquals } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { ModelSchema } from '../ModelSchema';
import { getLoadTypeOption } from './loadTypeOptions';
import { createMagnitudeFormatter } from './MovingLoadSchema';

const formatLocation = (location: number) => `${formatLengthToArchitecturalString(location)}`;
const formatLocationPoint = (location: Point2D) =>
  `(${formatLengthToArchitecturalString(location.x)}, ${formatLengthToArchitecturalString(location.y)})`;
export const getManualLoadDisplayName = ({ load }: { load: LoadDefinitionWrapper | LoadWrapper }) => {
  if (load.loadInsertionMethod === 'ProjectedCoordinates') {
    const { loadDefinition } = load;
    if (loadDefinition.name) {
      return `${loadDefinition.name}`;
    }
    const loadType = getLoadTypeOption(loadDefinition.loadType).name;
    const memberType = startCase(loadDefinition.memberType);
    if (loadDefinition.distribution === 'Concentrated') {
      const location = formatLocation(loadDefinition.coordinate);
      const formatMagnitude = createMagnitudeFormatter('Pounds');
      const magnitude = formatMagnitude(loadDefinition.magnitude);
      return `${loadType} ${memberType} ${magnitude} ${location}`;
    }

    const location = `(${formatLocation(loadDefinition.startCoordinate)} - ${formatLocation(
      loadDefinition.endCoordinate,
    )})`;
    const formatMagnitude = createMagnitudeFormatter(loadDefinition.magnitudeUnit);

    let isUniform: boolean;
    switch (loadDefinition.magnitudeUnit) {
      case 'PoundsPerLinearFoot':
        isUniform = distributedForceEquals(loadDefinition.startMagnitude, loadDefinition.endMagnitude);
        break;
      case 'PoundsPerSquareFoot':
        isUniform = stressEquals(loadDefinition.startMagnitude, loadDefinition.endMagnitude);
        break;
      default:
        assertExhaustiveSwitch(loadDefinition.magnitudeUnit);
    }
    const magnitude = isUniform
      ? formatMagnitude(loadDefinition.startMagnitude)
      : `${formatMagnitude(loadDefinition.startMagnitude)} - ${formatMagnitude(loadDefinition.endMagnitude)}`;
    return `${loadType} ${memberType} ${magnitude} ${location}`;
  }

  const manualLoad = load.load;

  if (manualLoad.name) {
    return `${manualLoad.name}`;
  }
  const loadType = getLoadTypeOption(manualLoad.loadType).name;
  const memberType = startCase(manualLoad.memberType);

  const location =
    manualLoad.distribution === 'Concentrated'
      ? formatLocationPoint(manualLoad.start)
      : `(${formatLocationPoint(manualLoad.start)} - ${formatLocationPoint(manualLoad.end)})`;

  const formatMagnitude = createMagnitudeFormatter(manualLoad.magnitudeUnit);

  let isUniform: boolean;
  switch (manualLoad.magnitudeUnit) {
    case 'Pounds':
      isUniform = true;
      break;
    case 'PoundsPerLinearFoot':
      isUniform = distributedForceEquals(manualLoad.startMagnitude, manualLoad.endMagnitude);
      break;
    case 'PoundsPerSquareFoot':
      isUniform = stressEquals(manualLoad.startMagnitude, manualLoad.endMagnitude);
      break;
    default:
      throw new Error('Unhandled magnitude unit');
  }
  const magnitude = isUniform
    ? formatMagnitude(manualLoad.startMagnitude)
    : `${formatMagnitude(manualLoad.startMagnitude)} - ${formatMagnitude(manualLoad.endMagnitude)}`;

  return `${loadType} ${memberType} ${magnitude} ${location}`;
};

export const ManualLoadSchema = new ModelSchema<ManualLoadSettings>('manualLoad', {
  category: ModelCategory.ComponentDesignData,

  pluralName: 'Manual Loads',
  displayName: getManualLoadDisplayName,

  properties: {},
});
